/**
 * TAB 2 — Game Owner Performance (requirement §12 to §21, AC-07 to AC-09).
 *
 * A hierarchy VC movement view, not a gameplay dashboard. The Game Owner table
 * deliberately carries no player counts, wins, losses, GGR or RTP (AC-08) —
 * those live in the Games tab.
 *
 * Implemented as a lazy drill-down with breadcrumbs (§21) rather than one wide
 * table: each level loads only when opened, so a Game Owner with thousands of
 * players never pulls its whole subtree.
 */

import * as React from "react";
import { Box, Grid, Breadcrumbs, Link, Typography, Button, Chip, TextField, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import {
  KpiCard,
  TreasuryFilters,
  DataTable,
  StatusChip,
  SectionTitle,
  ErrorBar,
  Loading,
  ExportButton,
  exportRows,
  fmtVC,
  fmtDateTime,
  humanise,
  filterInputSx,
  GOLD,
} from "../components/common";
import { getGameOwners, getHierarchyNode, getAccountTransactions } from "../api";

const TXN_DIRECTIONS = [
  { value: "all", label: "All Movement" },
  { value: "in", label: "Incoming Only" },
  { value: "out", label: "Outgoing Only" },
];

export const GameOwnerTab = ({ filters, onFiltersChange }) => {
  // `path` is the drill-down stack. Empty means we are on the Game Owner list.
  const [selected, setSelected] = React.useState(null);

  return selected ? (
    <AccountDetail
      accountId={selected}
      filters={filters}
      onFiltersChange={onFiltersChange}
      onNavigate={setSelected}
      onBack={() => setSelected(null)}
    />
  ) : (
    <GameOwnerList
      filters={filters}
      onFiltersChange={onFiltersChange}
      onOpen={setSelected}
    />
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// Game Owner main table (§13)
// ═════════════════════════════════════════════════════════════════════════════

const GameOwnerList = ({ filters, onFiltersChange, onOpen }) => {
  const [state, setState] = React.useState({ data: [], pagination: null, summary: null });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [limit, setLimit] = React.useState(10);
  const [sort, setSort] = React.useState({ field: "vcIssued", order: "DESC" });

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await getGameOwners({
        ...filters,
        search,
        page,
        limit,
        sortField: sort.field,
        sortOrder: sort.order,
      });
      setState(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters, search, page, limit, sort]);

  React.useEffect(() => {
    load();
  }, [load]);

  const columns = [
    { key: "gameOwnerId", label: "Game Owner ID", width: 130 },
    { key: "gameOwnerName", label: "Name", sortable: true },
    { key: "status", label: "Status", render: (r) => <StatusChip value={r.status} /> },
    { key: "vcIssued", label: "VC Issued", align: "right", sortable: true, render: (r) => fmtVC(r.vcIssued) },
    { key: "vcReturned", label: "VC Returned", align: "right", sortable: true, render: (r) => fmtVC(r.vcReturned) },
    {
      key: "vcInDistribution",
      label: "VC in Distribution",
      align: "right",
      sortable: true,
      render: (r) => fmtVC(r.vcInDistribution),
    },
    {
      key: "lastTransactionAt",
      label: "Last Transaction",
      sortable: true,
      render: (r) => fmtDateTime(r.lastTransactionAt),
    },
    {
      key: "action",
      label: "Action",
      align: "center",
      render: (r) => (
        <Button
          size="small"
          endIcon={<ChevronRightIcon />}
          onClick={(e) => {
            e.stopPropagation();
            onOpen(r.gameOwnerId);
          }}
          sx={{ color: GOLD, textTransform: "none", fontSize: "0.7rem" }}
        >
          View Details
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <TreasuryFilters
        filters={filters}
        onChange={onFiltersChange}
          extra={
          <>
            <TextField
              size="small"
              label="Search Game Owner"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              sx={filterInputSx}
            />
            <ExportButton
              disabled={!state.data?.length}
              onExport={(format) =>
                exportRows(
                  state.data.map((r) => ({
                    "Game Owner ID": r.gameOwnerId,
                    Name: r.gameOwnerName,
                    Status: r.status,
                    "VC Issued": r.vcIssued,
                    "VC Returned": r.vcReturned,
                    "VC in Distribution": r.vcInDistribution,
                    "Current Balance": r.currentBalance,
                    "Last Transaction": r.lastTransactionAt,
                  })),
                  "house-treasury-game-owners",
                  format
                )
              }
            />
          </>
        }
      />
      <ErrorBar error={error} onClose={() => setError("")} />

      {state.summary && (
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={6} md={3}>
            <KpiCard dense label="GAME OWNERS" value={state.summary.gameOwners} />
          </Grid>
          <Grid item xs={6} md={3}>
            <KpiCard dense label="TOTAL ISSUED" value={fmtVC(state.summary.totalIssued)} suffix="VC" />
          </Grid>
          <Grid item xs={6} md={3}>
            <KpiCard dense label="TOTAL RETURNED" value={fmtVC(state.summary.totalReturned)} suffix="VC" />
          </Grid>
          <Grid item xs={6} md={3}>
            <KpiCard
              dense
              label="TOTAL IN DISTRIBUTION"
              value={fmtVC(state.summary.totalInDistribution)}
              suffix="VC"
            />
          </Grid>
        </Grid>
      )}

      <DataTable
        columns={columns}
        rows={state.data}
        loading={loading}
        pagination={state.pagination}
        onPageChange={setPage}
        onLimitChange={setLimit}
        sortField={sort.field}
        sortOrder={sort.order}
        onSortChange={(field, order) => setSort({ field, order })}
        onRowClick={(r) => onOpen(r.gameOwnerId)}
        emptyMessage="No Game Owners found."
      />
    </Box>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// Drill-down detail (§14 to §20)
// ═════════════════════════════════════════════════════════════════════════════

const AccountDetail = ({ accountId, filters, onFiltersChange, onNavigate, onBack }) => {
  const [node, setNode] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [childPage, setChildPage] = React.useState(1);
  const [childLimit, setChildLimit] = React.useState(10);
  const [childSearch, setChildSearch] = React.useState("");

  React.useEffect(() => {
    setChildPage(1);
    setChildSearch("");
  }, [accountId]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const result = await getHierarchyNode({
          ...filters,
          accountId,
          childPage,
          childLimit,
          childSearch,
        });
        if (!cancelled) setNode(result);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accountId, filters, childPage, childLimit, childSearch]);

  const o = node?.overview || {};
  const children = node?.children || {};
  const isPlayer = o.accountType === "Player";

  const childColumns = [
    { key: "accountId", label: "ID", width: 120 },
    { key: "accountName", label: "Name" },
    { key: "status", label: "Status", render: (r) => <StatusChip value={r.status} /> },
    { key: "vcReceived", label: "VC Received", align: "right", render: (r) => fmtVC(r.vcReceived) },
    ...(children.role === "Player"
      ? [
          { key: "vcWagered", label: "Wagered", align: "right", render: (r) => fmtVC(r.vcWagered) },
          { key: "vcWon", label: "Won", align: "right", render: (r) => fmtVC(r.vcWon) },
          { key: "bonusReceived", label: "Bonus", align: "right", render: (r) => fmtVC(r.bonusReceived) },
          { key: "vcRedeemed", label: "Redeemed", align: "right", render: (r) => fmtVC(r.vcRedeemed) },
        ]
      : [
          { key: "vcTransferred", label: "VC Transferred", align: "right", render: (r) => fmtVC(r.vcTransferred) },
          { key: "vcReturned", label: "VC Returned", align: "right", render: (r) => fmtVC(r.vcReturned) },
        ]),
    { key: "currentBalance", label: "Current Balance", align: "right", render: (r) => fmtVC(r.currentBalance) },
    { key: "lastTransactionAt", label: "Last Transaction", render: (r) => fmtDateTime(r.lastTransactionAt) },
    {
      key: "action",
      label: "Action",
      align: "center",
      render: (r) => (
        <Button
          size="small"
          endIcon={<ChevronRightIcon />}
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(r.accountId);
          }}
          sx={{ color: GOLD, textTransform: "none", fontSize: "0.7rem" }}
        >
          {r.hasChildren ? "Drill Down" : "View"}
        </Button>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <Button
          size="small"
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{ color: GOLD, textTransform: "none" }}
        >
          Game Owners
        </Button>
        <Breadcrumbs
          separator={<ChevronRightIcon sx={{ fontSize: 14, color: "#666" }} />}
          sx={{ "& .MuiBreadcrumbs-li": { fontSize: "0.75rem" } }}
        >
          {(node?.breadcrumb || []).map((crumb, i, arr) =>
            i === arr.length - 1 ? (
              <Typography key={crumb.accountId} sx={{ color: GOLD, fontSize: "0.75rem" }}>
                {crumb.accountName} ({crumb.accountType})
              </Typography>
            ) : (
              <Link
                key={crumb.accountId}
                component="button"
                onClick={() => onNavigate(crumb.accountId)}
                sx={{ color: "#9E9E9E", fontSize: "0.75rem", textDecoration: "none" }}
              >
                {crumb.accountName}
              </Link>
            )
          )}
        </Breadcrumbs>
      </Box>

      <TreasuryFilters filters={filters} onChange={onFiltersChange} />
      <ErrorBar error={error} onClose={() => setError("")} />

      {loading && !node ? (
        <Loading />
      ) : (
        <>
          <SectionTitle
            action={<Chip size="small" label={o.accountType} sx={{ backgroundColor: "#242426", color: GOLD }} />}
          >
            {o.accountName} — VC Position
          </SectionTitle>

          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            <Grid item xs={6} md={2}>
              <KpiCard dense label="CURRENT BALANCE" value={fmtVC(o.currentBalance)} suffix="VC" accent={GOLD} />
            </Grid>
            <Grid item xs={6} md={2}>
              <KpiCard dense label="VC RECEIVED" value={fmtVC(o.vcReceived)} suffix="VC" />
            </Grid>
            {isPlayer ? (
              <>
                <Grid item xs={6} md={2}>
                  <KpiCard dense label="VC WAGERED" value={fmtVC(o.vcWagered)} suffix="VC" />
                </Grid>
                <Grid item xs={6} md={2}>
                  <KpiCard dense label="VC WON" value={fmtVC(o.vcWon)} suffix="VC" />
                </Grid>
                <Grid item xs={6} md={2}>
                  <KpiCard dense label="VC LOST" value={fmtVC(o.vcLost)} suffix="VC" />
                </Grid>
                <Grid item xs={6} md={2}>
                  <KpiCard dense label="BONUS RECEIVED" value={fmtVC(o.bonusReceived)} suffix="VC" />
                </Grid>
                <Grid item xs={6} md={2}>
                  <KpiCard dense label="VC REDEEMED" value={fmtVC(o.vcRedeemed)} suffix="VC" />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={6} md={2}>
                  <KpiCard dense label="VC TRANSFERRED" value={fmtVC(o.vcTransferred)} suffix="VC" />
                </Grid>
                <Grid item xs={6} md={2}>
                  <KpiCard dense label="VC RETURNED" value={fmtVC(o.vcReturned)} suffix="VC" />
                </Grid>
                <Grid item xs={6} md={2}>
                  <KpiCard
                    dense
                    label="VC IN DISTRIBUTION"
                    value={fmtVC(o.vcInDistribution)}
                    suffix="VC"
                    hint="This account plus everything beneath it, excluding player wallets."
                  />
                </Grid>
                <Grid item xs={6} md={2}>
                  <KpiCard dense label="VC IN PLAYERS" value={fmtVC(o.vcInPlayers)} suffix="VC" />
                </Grid>
                {o.accountType === "Agent" && (
                  <Grid item xs={6} md={2}>
                    <KpiCard
                      dense
                      label="FROM REDEMPTION"
                      value={fmtVC(o.vcFromPlayerRedemption)}
                      suffix="VC"
                      hint="VC that came back from this agent's players."
                    />
                  </Grid>
                )}
              </>
            )}
          </Grid>

          {children.role && (
            <>
              <SectionTitle
                action={
                  <TextField
                    size="small"
                    label={`Search ${children.role}`}
                    value={childSearch}
                    onChange={(e) => {
                      setChildSearch(e.target.value);
                      setChildPage(1);
                    }}
                    sx={filterInputSx}
                  />
                }
              >
                {children.role}s
              </SectionTitle>
              <Box sx={{ mb: 3 }}>
                <DataTable
                  dense
                  columns={childColumns}
                  rows={children.data}
                  loading={loading}
                  pagination={children.pagination}
                  onPageChange={setChildPage}
                  onLimitChange={setChildLimit}
                  onRowClick={(r) => onNavigate(r.accountId)}
                  emptyMessage={`No ${children.role}s under this account.`}
                />
              </Box>
            </>
          )}

          <AccountTransactions accountId={accountId} filters={filters} />
        </>
      )}
    </Box>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// Transaction log for one account (§16, AC-09)
// ═════════════════════════════════════════════════════════════════════════════

const AccountTransactions = ({ accountId, filters }) => {
  const [state, setState] = React.useState({ data: [], pagination: null });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [limit, setLimit] = React.useState(10);
  const [direction, setDirection] = React.useState("all");

  React.useEffect(() => {
    setPage(1);
  }, [accountId, direction]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const result = await getAccountTransactions({
          ...filters,
          accountId,
          direction,
          page,
          limit,
        });
        if (!cancelled) setState(result);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accountId, filters, direction, page, limit]);

  const columns = [
    { key: "occurredAt", label: "Date/Time", render: (r) => fmtDateTime(r.occurredAt) },
    { key: "txnId", label: "Transaction ID", width: 190 },
    { key: "transactionType", label: "Type", render: (r) => humanise(r.transactionType) },
    { key: "from", label: "From", render: (r) => r.from?.name || r.from?.id || "-" },
    { key: "to", label: "To", render: (r) => r.to?.name || r.to?.id || "-" },
    {
      key: "amount",
      label: "Amount",
      align: "right",
      render: (r) => (
        <span style={{ color: r.direction === "IN" ? "#A5D6A7" : "#EF9A9A" }}>
          {r.direction === "IN" ? "+" : "-"}
          {fmtVC(r.amount)}
        </span>
      ),
    },
    { key: "status", label: "Status", render: (r) => <StatusChip value={r.status} /> },
    { key: "reference", label: "Reference", width: 160 },
  ];

  return (
    <>
      <SectionTitle
        action={
          <Box sx={{ display: "flex", gap: 1 }}>
            <FormControl size="small" sx={filterInputSx}>
              <InputLabel>Direction</InputLabel>
              <Select
                label="Direction"
                value={direction}
                onChange={(e) => setDirection(e.target.value)}
              >
                {TXN_DIRECTIONS.map((d) => (
                  <MenuItem key={d.value} value={d.value}>
                    {d.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <ExportButton
              disabled={!state.data?.length}
              onExport={(format) =>
                exportRows(
                  state.data.map((r) => ({
                    "Date/Time": r.occurredAt,
                    "Transaction ID": r.txnId,
                    Type: r.transactionType,
                    Direction: r.direction,
                    From: r.from?.name,
                    To: r.to?.name,
                    Amount: r.amount,
                    Status: r.status,
                    Reference: r.reference,
                  })),
                  `house-treasury-account-${accountId}`,
                  format
                )
              }
            />
          </Box>
        }
      >
        VC Transaction History
      </SectionTitle>
      <ErrorBar error={error} onClose={() => setError("")} />
      <DataTable
        dense
        columns={columns}
        rows={state.data}
        loading={loading}
        pagination={state.pagination}
        onPageChange={setPage}
        onLimitChange={setLimit}
        emptyMessage="No VC movement recorded for this account in the selected period."
      />
    </>
  );
};

export default GameOwnerTab;
